import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Ban, Send, ArrowLeft, AlertTriangle, AlertCircle, ShieldOff } from "lucide-react";

const TARGET_META = {
  ban:     { label: "Account ban",      icon: Ban,           color: "var(--brand-primary)" },
  warning: { label: "Warning",          icon: AlertTriangle, color: "#B45309" },
  limit:   { label: "Account limit",    icon: AlertCircle,   color: "#B45309" },
  denial:  { label: "Recruiter denial", icon: ShieldOff,     color: "var(--brand-primary)" },
};

export default function Appeal() {
  const { user, refresh } = useAuth();
  const [appeals, setAppeals] = useState([]);
  const [history, setHistory] = useState(null);
  const [composer, setComposer] = useState(null); // { target_type, target_id, context_label }
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try {
      const [a, h] = await Promise.all([
        api.get("/appeals/mine").catch(() => ({ data: [] })),
        api.get("/users/me/moderation-history").catch(() => ({ data: null })),
      ]);
      setAppeals(a.data || []);
      setHistory(h.data);
    } catch (e) { setError(formatApiError(e.response?.data?.detail) || e.message); }
  };

  useEffect(() => { if (user && user !== false) load(); }, [user]);

  const openAppealsByKey = useMemo(() => {
    const map = {};
    (appeals || []).forEach((a) => {
      if (a.status === "open") {
        const k = `${a.target_type || "ban"}:${a.target_id || ""}`;
        map[k] = a;
      }
    });
    return map;
  }, [appeals]);

  if (!user || user === false) return <div className="p-16">Loading…</div>;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!composer) return;
    setError(""); setSuccess(""); setLoading(true);
    try {
      await api.post("/appeals", {
        message,
        target_type: composer.target_type,
        target_id: composer.target_id || undefined,
      });
      setMessage("");
      setComposer(null);
      setSuccess("Your appeal has been submitted. A moderator will review it.");
      await refresh();
      load();
    } catch (e) { setError(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };

  const items = [];
  if (history?.is_banned) items.push({ target_type: "ban", target_id: "", title: history.ban_reason || "Account banned", subtitle: history.banned_at ? new Date(history.banned_at).toLocaleString() : "" });
  if (history?.is_limited) items.push({ target_type: "limit", target_id: "", title: history.limited_reason || "Account limited", subtitle: history.limited_at ? new Date(history.limited_at).toLocaleString() : "" });
  if (history?.is_denied) items.push({ target_type: "denial", target_id: "", title: history.denial_reason || "Recruiter denied", subtitle: history.denied_at ? new Date(history.denied_at).toLocaleString() : "" });
  (history?.warnings || []).filter((w) => !w.voided).forEach((w) => {
    items.push({ target_type: "warning", target_id: w.id, title: w.reason || "Warning", subtitle: `${w.at ? new Date(w.at).toLocaleString() : ""}${w.note ? ` — ${w.note}` : ""}` });
  });

  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-10 py-12">
      <Link to="/" className="inline-flex items-center gap-1 text-sm font-semibold hover:text-[var(--brand-primary)]">
        <ArrowLeft className="w-4 h-4" /> Home
      </Link>
      <div className="mt-6">
        <div className="label-eyebrow flex items-center gap-1.5"><AlertTriangle className="w-3 h-3 text-[var(--brand-primary)]" /> Appeals</div>
        <h1 className="font-display font-black text-4xl mt-3 tracking-tight">Appeal a moderation action.</h1>
        <p className="text-sm text-[#5C5C5C] mt-2">You can appeal any active warning, limit, denial, or account ban. A moderator will review your appeal within five business days.</p>
      </div>

      {items.length === 0 && (
        <div data-testid="appeal-none" className="mt-10 text-center text-[#5C5C5C] py-10 border border-[#E2E0D8]">
          Nothing to appeal — your account is in good standing.
        </div>
      )}

      {items.length > 0 && (
        <div className="mt-8 grid gap-px bg-[#E2E0D8] border border-[#E2E0D8]">
          {items.map((it) => {
            const meta = TARGET_META[it.target_type];
            const Icon = meta.icon;
            const key = `${it.target_type}:${it.target_id || ""}`;
            const openAppeal = openAppealsByKey[key];
            return (
              <div key={key} data-testid={`appealable-${key}`} className="bg-[#FDFCF8] p-5 flex items-start justify-between gap-4">
                <div className="flex items-start gap-3 min-w-0">
                  <Icon className="w-5 h-5 mt-0.5" style={{ color: meta.color }} />
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: meta.color }}>{meta.label}</div>
                    <div className="font-display font-bold mt-0.5 truncate">{it.title}</div>
                    {it.subtitle && <div className="text-xs text-[#5C5C5C] mt-0.5">{it.subtitle}</div>}
                  </div>
                </div>
                <div className="flex-shrink-0">
                  {openAppeal ? (
                    <span data-testid={`appeal-pending-${key}`} className="text-[10px] uppercase tracking-widest font-semibold bg-[var(--brand-primary)] text-white px-2 py-1">Pending</span>
                  ) : (
                    <button
                      onClick={() => { setComposer(it); setMessage(""); setError(""); setSuccess(""); }}
                      data-testid={`appeal-btn-${key}`}
                      className="text-sm border border-[#0F0F0F] px-3 py-1.5 hover:bg-[#0F0F0F] hover:text-[#FDFCF8] inline-flex items-center gap-1.5"
                    >
                      <Send className="w-3.5 h-3.5" /> Appeal
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {composer && (
        <div className="fixed inset-0 z-50 bg-[#0F0F0F]/60 flex items-center justify-center p-6" onClick={() => setComposer(null)} data-testid="appeal-modal">
          <form onSubmit={submit} className="bg-[#FDFCF8] border border-[#0F0F0F] p-8 max-w-xl w-full" onClick={(e) => e.stopPropagation()}>
            <div className="label-eyebrow">{TARGET_META[composer.target_type].label}</div>
            <h3 className="font-display font-black text-2xl mt-1">Explain your appeal.</h3>
            <p className="text-sm text-[#5C5C5C] mt-2">Subject: <em>{composer.title}</em></p>
            <label className="label-eyebrow block mb-1.5 mt-5">Your appeal (min 20 chars)</label>
            <textarea
              data-testid="appeal-message"
              required minLength={20} rows={6}
              value={message} onChange={(e) => setMessage(e.target.value)}
              className="w-full border border-[#0F0F0F] bg-transparent px-3 py-2"
              placeholder="Provide context, evidence, what's changed — anything that helps a moderator re-evaluate."
            />
            {error && <div data-testid="appeal-error" className="text-sm text-[var(--brand-primary)] mt-2">{error}</div>}
            <div className="flex gap-2 mt-4">
              <button disabled={loading || message.length < 20} data-testid="appeal-submit" type="submit" className="btn-terra flex-1">
                <Send className="w-4 h-4" /> {loading ? "Submitting…" : "Submit appeal"}
              </button>
              <button type="button" onClick={() => setComposer(null)} className="btn-ghost">Cancel</button>
            </div>
          </form>
        </div>
      )}

      {success && <div data-testid="appeal-success" className="mt-6 text-sm text-green-700">{success}</div>}

      {appeals.length > 0 && (
        <div className="mt-12">
          <div className="label-eyebrow">All my appeals</div>
          <div className="mt-3 grid gap-px bg-[#E2E0D8] border border-[#E2E0D8]">
            {appeals.map((a) => (
              <div key={a.appeal_id} className="bg-[#FDFCF8] p-4">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[10px] uppercase tracking-widest font-semibold px-1.5 py-0.5 ${a.status === "open" ? "bg-[var(--brand-primary)] text-white" : "bg-[#E2E0D8]"}`}>{a.status}{a.action ? ` · ${a.action}` : ""}</span>
                  <span className="text-[10px] uppercase tracking-widest font-semibold border border-[#0F0F0F] px-1.5 py-0.5">{a.target_type || "ban"}</span>
                  <span className="text-xs text-[#5C5C5C]">{new Date(a.created_at).toLocaleString()}</span>
                </div>
                <p className="text-sm mt-2 whitespace-pre-wrap">{a.message}</p>
                {a.resolution_note && <div className="text-xs text-[#5C5C5C] mt-2 italic">Moderator note: {a.resolution_note}</div>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
